/* V163.4 — Ad-Launch Readiness Checklist
 * Single-screen "am I ready to spend €1 on FB?" widget.
 * Reads /api/ad-creative/launch-readiness which checks env vars + DB state.
 */
import { useEffect, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STATUS_STYLE = {
  ok: { bg: "bg-emerald-950/40", border: "border-emerald-700", text: "text-emerald-300", icon: "✅" },
  warn: { bg: "bg-amber-950/40", border: "border-amber-700", text: "text-amber-300", icon: "⏳" },
  fail: { bg: "bg-rose-950/40", border: "border-rose-700", text: "text-rose-300", icon: "❌" },
};

export default function AdminLaunchChecklistTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const load = () => {
    const token = localStorage.getItem("admin_token") || "";
    if (!token) {
      setErr("Admin token chýba. Prihlás sa znova.");
      return;
    }
    setLoading(true);
    setErr("");
    axios
      .get(`${API}/ad-creative/launch-readiness`, { headers: { "X-Admin-Token": token } })
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "load_failed"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  if (loading && !data) {
    return <div className="p-6 text-zinc-500">Načítavam launch readiness…</div>;
  }
  if (err) {
    return (
      <div className="p-6">
        <div className="bg-rose-950/40 border border-rose-700 text-rose-300 p-4 text-sm" data-testid="launch-checklist-error">
          {err}
        </div>
      </div>
    );
  }
  if (!data) return null;

  const score = data.score_pct || 0;
  const scoreColor = score >= 90 ? "text-emerald-400" : score >= 60 ? "text-amber-400" : "text-rose-400";

  return (
    <div className="p-4 space-y-5" data-testid="launch-checklist-tab">
      <header>
        <h2 className="text-xl font-bold flex items-center gap-2">
          🚀 Launch Readiness Checklist
          <span className="text-[10px] tracking-widest bg-amber-500 text-black px-2 py-0.5 font-black uppercase">V163.4</span>
        </h2>
        <p className="text-sm text-zinc-400 mt-1">
          Single source of truth: "som ready spustiť FB Ads kampaň?". Sleduje 8 kritických bodov a vracia recommendation per denný budget.
        </p>
      </header>

      {/* Big score */}
      <section className="border-2 border-zinc-800 bg-gradient-to-br from-zinc-950 to-black p-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-black">Launch Score</div>
            <div className={`text-6xl font-black mt-1 ${scoreColor}`} data-testid="launch-score">
              {score}%
            </div>
            <div className="text-xs text-zinc-400 mt-1">
              {data.ok} / {data.total} bodov ✅ · {data.warn} ⏳ · {data.fail} ❌
            </div>
          </div>
          <div className="flex-1 max-w-md">
            <div className="text-[10px] uppercase tracking-widest text-amber-400 font-black mb-1">💰 Budget recommendation</div>
            <div className="text-sm text-zinc-200 leading-relaxed" data-testid="launch-budget-rec">
              {data.budget_recommendation}
            </div>
            {data.ready_to_launch && (
              <div className="mt-2 inline-block bg-emerald-500 text-black font-black uppercase tracking-wider text-xs px-3 py-1.5">
                ✓ READY TO LAUNCH
              </div>
            )}
          </div>
          <button
            onClick={load}
            className="text-xs px-3 py-1.5 border border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            data-testid="launch-checklist-refresh"
          >
            🔄 Refresh
          </button>
        </div>
      </section>

      {/* Checklist items */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {data.checks.map((c) => {
          const s = STATUS_STYLE[c.status] || STATUS_STYLE.warn;
          return (
            <div
              key={c.key}
              className={`border ${s.border} ${s.bg} p-3`}
              data-testid={`launch-check-${c.key}`}
            >
              <div className="flex items-center justify-between mb-1">
                <div className="font-bold text-zinc-100 text-sm">{c.label}</div>
                <div className={`text-xs font-mono font-bold ${s.text}`}>
                  {s.icon} {c.value}
                </div>
              </div>
              <div className="text-[11px] text-zinc-400 leading-snug">{c.hint}</div>
            </div>
          );
        })}
      </section>

      {/* Quick-action footer */}
      <section className="border border-zinc-800 bg-zinc-950 p-4">
        <h3 className="text-sm uppercase tracking-widest text-zinc-500 font-black mb-2">🎯 Ďalšie kroky</h3>
        <ul className="text-xs text-zinc-300 space-y-1 list-disc list-inside leading-relaxed">
          <li>Otvor <strong className="text-amber-300">🎨 Ad Creative Studio</strong> → vygeneruj 3 PNG variants (handwritten + vs + before-after)</li>
          <li>FB Ads Manager → Custom Audience: SK + CZ e-shop owners, vek 28-55, záujmy "Shopify, WooCommerce, podnikanie"</li>
          <li>Budget: začni €15-30/deň test, po 3 dňoch posúď CPA</li>
          <li>UTM parametry: <code className="bg-zinc-800 px-1">?utm_source=fb&utm_medium=cpc&utm_campaign=launch_v1</code></li>
          <li>Konverzia tracking: pixel + CAPI → eventy <code className="bg-zinc-800 px-1">PageView, Lead, CompleteRegistration, Purchase</code></li>
        </ul>
      </section>
    </div>
  );
}
